/* eslint-disable max-classes-per-file */

import { LitElement } from "lit"
import { createRef, Ref } from "lit/directives/ref.js"
import { InputMixinInterface } from "./InputMixin.js"

type Constructable<T = Record<string, unknown>> = new (...args: any[]) => T

export declare class TextSelectableMixinInterface {
  protected textSelectableRef: Ref<HTMLInputElement | HTMLTextAreaElement>
  set selectionStart(value: number | null)
  get selectionStart(): number | null
  set selectionEnd(value: number | null)
  get selectionEnd(): number | null
  select(): void
  setSelectionRange(start: number | null, end: number | null, direction?: "forward" | "backward" | "none"): void
  setRangeText(
    replacement: string,
    start?: number,
    end?: number,
    selectMode?: "select" | "start" | "end" | "preserve"
  ): void
}

export function TextSelectableMixin<T extends Constructable<InputMixinInterface & LitElement>>(superClass: T) {
  class TextSelectableElement extends superClass {
    protected textSelectableRef = createRef<HTMLInputElement | HTMLTextAreaElement>()

    /**
     * Gets or sets the starting position or offset of a text selection.
     *
     * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/selectionStart)
     */
    set selectionStart(value: number | null) {
      if (this.textSelectableRef.value) {
        this.textSelectableRef.value.selectionStart = value
      }
    }

    get selectionStart() {
      return this.textSelectableRef.value?.selectionStart ?? null
    }

    /**
     * Gets or sets the end position or offset of a text selection.
     *
     * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/selectionEnd)
     */
    set selectionEnd(value: number | null) {
      if (this.textSelectableRef.value) {
        this.textSelectableRef.value.selectionEnd = value
      }
    }

    get selectionEnd() {
      return this.textSelectableRef.value?.selectionEnd ?? null
    }

    /**
     * Gets or sets the direction in which selection occurred.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/selectionDirection)
     */
    set selectionDirection(value: "forward" | "backward" | "none" | null) {
      if (this.textSelectableRef.value) {
        this.textSelectableRef.value.selectionDirection = value
      }
    }

    get selectionDirection() {
      return this.textSelectableRef.value?.selectionDirection ?? null
    }

    /**
     * Selects all the text in the text field.
     *
     * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select)
     */
    select() {
      if (this.textSelectableRef.value) {
        this.textSelectableRef.value.select()
      }
    }

    /**
     * Sets the start and end positions of a selection in the text field.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/setSelectionRange)
     *
     * @param start The offset into the text field for the start of the selection.
     * @param end The offset into the text field for the end of the selection.
     * @param direction The direction in which the selection is performed.
     */
    setSelectionRange(start: number | null, end: number | null, direction?: "forward" | "backward" | "none") {
      if (this.textSelectableRef.value) {
        this.textSelectableRef.value.setSelectionRange(start, end, direction)
      }
    }

    /**
     * Replaces a range of text with a new string.
     *
     * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/setRangeText)
     *
     * @param replacement The string to insert.
     * @param start The 0-based index of the first character to replace. Defaults to the current `selectionStart` value (the start of the user's current selection).
     * @param end The 0-based index of the character after the last character to replace. Defaults to the current `selectionEnd` value (the end of the user's current selection).
     * @param selectMode A string defining how the selection should be set after the text has been replaced.
     */
    setRangeText(
      replacement: string,
      start?: number,
      end?: number,
      selectMode: "select" | "start" | "end" | "preserve" = "preserve"
    ) {
      if (this.textSelectableRef.value) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const selectionStart = start ?? this.textSelectableRef.value.selectionStart!
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const selectionEnd = end ?? this.textSelectableRef.value.selectionEnd!

        this.textSelectableRef.value.setRangeText(replacement, selectionStart, selectionEnd, selectMode)

        if (this.value !== this.textSelectableRef.value.value) {
          this.value = this.textSelectableRef.value.value
        }
      }
    }

    /**
     * Returns the native `<input>` or `<textarea>` element used under the hood.
     */
    async getNativeElement() {
      // wait for the element to be rendered
      await this.updateComplete

      return this.textSelectableRef.value
    }
  }

  return TextSelectableElement as unknown as Constructable<TextSelectableMixinInterface> & T
}
